/**
 * A page's filters as a collapsible LEFT column — the shape the Search & Track and Camera List
 * pages already use, made generic so every list page reads the same way:
 *
 *   <PageWithFilters filters={<FilterColumn …>sections</FilterColumn>}>
 *     results
 *   </PageWithFilters>
 *
 * The column folds to a 44px rail (with the active-filter count) and remembers that per page in
 * localStorage under `trinetra.filters.<storageKey>`. Its controls stay MOUNTED while folded, so
 * a half-typed search survives a fold. It is sticky and full height, scrolling inside itself.
 *
 * Put filters here; keep page ACTIONS (Add, Export, Refresh) and view toggles in the results'
 * own toolbar or in <PageTitle>. Removable active-filter chips belong above the results, never
 * inside the column — a folded column must not hide why the list is short.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { AdjustmentsHorizontalIcon, CheckIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { HidePaneButton, PaneRail } from '../search/PaneControls';
import { PAGE_FILL_H } from './TitleBar';

const storageKeyFor = (key: string) => `trinetra.filters.${key}`;

/** Open/closed state for a FilterColumn, remembered per page. */
export function useFilterColumn(storageKey: string, defaultOpen = true) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKeyFor(storageKey));
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKeyFor(storageKey), next ? '1' : '0'); } catch { /* private window */ }
      return next;
    });
  }, [storageKey]);
  return { open, toggle };
}

export interface FilterColumnProps {
  open: boolean;
  onToggle: () => void;
  /** Column heading, e.g. "Alert filters". */
  label?: string;
  /** Filters currently narrowing the list — shown on the folded rail and beside "Clear". */
  activeCount?: number;
  onClearAll?: () => void;
  children: ReactNode;
  /** Height classes; defaults to filling the screen under the title bar. */
  heightClass?: string;
  /** Extra line under the heading row (e.g. a scope note). */
  header?: ReactNode;
  /** Extra classes on the root, e.g. `hidden lg:block` where a page keeps a bar on phones. */
  className?: string;
}

export function FilterColumn({
  open, onToggle, label = 'Filters', activeCount = 0, onClearAll, children,
  heightClass = PAGE_FILL_H, header = null, className = '',
}: FilterColumnProps) {
  return (
    <div
      className={`sticky top-0 shrink-0 self-start overflow-hidden rounded-lg border border-border bg-card shadow-card transition-[width] duration-300 ease-in-out ${heightClass} ${className} ${
        open ? 'w-[15rem] xl:w-[16.5rem]' : 'w-11'
      }`}
    >
      <aside
        aria-label={label}
        aria-hidden={!open}
        className={`flex h-full w-[15rem] flex-col transition-opacity duration-200 xl:w-[16.5rem] ${open ? 'opacity-100' : 'invisible opacity-0'}`}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1.5">
          <span className="flex min-w-0 items-center gap-1.5 truncate text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <AdjustmentsHorizontalIcon className="h-3.5 w-3.5 shrink-0" />
            {label}
          </span>
          <span className="flex items-center gap-2">
            {onClearAll && activeCount > 0 && (
              <button type="button" onClick={onClearAll}
                className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
                Clear ({activeCount})
              </button>
            )}
            <HidePaneButton side="left" label={label.toLowerCase()} onClick={onToggle} />
          </span>
        </div>
        {header}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
          {children}
        </div>
      </aside>
      {!open && (
        <PaneRail
          label={label}
          Icon={AdjustmentsHorizontalIcon}
          count={activeCount}
          onExpand={onToggle}
          opens="right"
          className="absolute inset-0"
        />
      )}
    </div>
  );
}

/** One titled block in a FilterColumn, with an optional action (usually "Clear") on the right. */
export function FilterSection({ title, hint = null, action = null, children }: {
  title: string; hint?: ReactNode; action?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
          {hint && <span className="ml-1.5 font-normal normal-case tracking-normal">{hint}</span>}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** A full-width checkable row (checkbox, or radio dot with `radio`), optional swatch and count. */
export function FilterCheck({ checked, onClick, label, count = null, swatch = null, radio = false }: {
  checked: boolean; onClick: () => void; label: ReactNode; count?: number | null; swatch?: string | null; radio?: boolean;
}) {
  return (
    <button
      type="button"
      role={radio ? 'radio' : 'checkbox'}
      aria-checked={checked}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors ${
        checked ? 'bg-brand-light font-medium text-brand' : 'text-foreground hover:bg-muted'
      }`}
    >
      {radio ? (
        <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${checked ? 'border-brand' : 'border-border'}`}>
          {checked && <span className="h-1.5 w-1.5 rounded-full bg-brand" />}
        </span>
      ) : (
        <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${checked ? 'border-brand bg-brand text-white' : 'border-border'}`}>
          {checked && <CheckIcon className="h-2.5 w-2.5 stroke-[3]" />}
        </span>
      )}
      {swatch && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: swatch }} />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== null && <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{count}</span>}
    </button>
  );
}

/** The search box used at the top of most filter columns. */
export function FilterSearch({ value, onChange, placeholder = 'Search…' }: {
  value: string; onChange: (value: string) => void; placeholder?: string;
}) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-input py-1.5 pl-8 pr-7 text-[13px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
      />
      {value && (
        <button type="button" onClick={() => onChange('')} aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground">
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/** Class for a native <select>/<input> inside a filter column, full width. */
export const FILTER_CONTROL =
  'w-full rounded-lg border border-border bg-input px-2.5 py-1.5 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

/**
 * Filter column + results side by side. `filters` is usually a <FilterColumn>; the results side
 * gets the remaining width and must not set its own horizontal margin.
 */
export function PageWithFilters({ filters, children, className = '' }: {
  filters: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      {filters}
      <div className="min-w-0 flex-1 space-y-3">{children}</div>
    </div>
  );
}
